import { getAskAiTier, isTrialEnabled, type AskAiTier } from '@readme/backend/iso';
import { getPlanFeature } from '@readme/plans';
import { useContext } from 'react';

import { ProjectContext } from '@core/context';
import { useProjectStore } from '@core/store';

export interface AskAiState {
  canTrialOwlbot: boolean;
  enabled: boolean;
  isOn: boolean;
  isPaying: boolean;
  renderMode: 'off' | 'owlbot' | 'askai';
  state: AskAiTier;
  trialingOwlbot: boolean;
}

/**
 * Reactively derives the current Ask AI feature state from project plan,
 * plan override, paying status, trial status, and flags.
 */
export default function useAskAIState(): AskAiState {
  const { project } = useContext(ProjectContext);
  const { isPaying, newExperience, owlbotEnabled, owlbotTrialPaying, plan, planOverride } = useProjectStore(s => ({
    isPaying: s.data.ai.owlbot.is_paying,
    newExperience: s.data.ai.owlbot.new_experience,
    owlbotEnabled: s.data.ai.owlbot.enabled,
    owlbotTrialPaying: s.data.ai.owlbot.trial.is_paying,
    plan: s.plan.plan,
    planOverride: s.plan.override,
  }));

  const isEnterpriseChild = !!project?.parent;

  const isEnterprise =
    !!project?.childrenProjects?.length ||
    isEnterpriseChild ||
    !!project?.flags?.enterprise ||
    planOverride === 'enterprise';

  const isParentOwlbotEnabled = !!project?.parent?.owlbot?.enabled;
  const isParentOwlbotNewExperience = project?.parent?.owlbot?.newExperience;

  const isProjectTrialActive = isTrialEnabled(project);

  // ASKAI TRIAL !== PROJECT TRIAL
  // On project trial, users has askai-lite. They can active AskAI trial, which will give them askai-full.
  // AskAI trial can only be active if project trial is active
  const owlbotTrialActive = isProjectTrialActive && owlbotTrialPaying;

  const canTrialOwlbot = isProjectTrialActive && !owlbotTrialPaying;

  const enterpriseStatus = isEnterprise ? (isEnterpriseChild ? 'child' : 'single') : 'none';

  const state = getAskAiTier({
    enterpriseStatus,
    hasBoosterPack: isPaying,
    isAskAiFullTrial: owlbotTrialActive,
    isProjectTrial: isProjectTrialActive,
    planHasAskAi: getPlanFeature(planOverride || plan, 'askai'),
    hideAiFeatures: project?.flags?.hideAiFeatures,
    askAiOverride: project?.flags?.askAiOverride,
    parentAskAiOverride: project?.parent?.flags?.askAiOverride,
  });

  const isOn = (isEnterpriseChild ? isParentOwlbotEnabled : owlbotEnabled) && state !== 'off';

  const renderMode: 'off' | 'owlbot' | 'askai' = !isOn
    ? 'off'
    : isEnterpriseChild
      ? isParentOwlbotNewExperience
        ? 'askai'
        : 'owlbot'
      : newExperience
        ? 'askai'
        : 'owlbot';

  return {
    canTrialOwlbot,
    state,
    enabled: state !== 'off',
    trialingOwlbot: owlbotTrialActive,
    isPaying,
    isOn,
    renderMode,
  };
}
